import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { useTheme } from '@mui/material';
import { useEffect } from 'react';
import { BackdropImage } from '#components/BackdropImage/index.tsx';
import { TabsCarousel } from '#components/TabsCarousel/index.tsx';
import { mapScreenTypeToSectionType } from '#helpers/enumMappers.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { TrendingTabPanel } from './TrendingTabPanel.tsx';
import { formatTrending } from './helpers/formatTrending.ts';
import { type TrendingCarouselProps, type TrendingData } from './types.ts';

const GET_TRENDING = "query GetTrending(\n  $after: String\n  $before: String\n  $first: Int = 20\n  $last: Int\n  $mediaType: TrendingMediaType = ALL\n  $timeWindow: TrendingTimeWindow = WEEK\n) {\n  trending(\n    after: $after,\n    before: $before,\n    first: $first,\n    last: $last,\n    mediaType: $mediaType,\n    timeWindow: $timeWindow,\n  ) {\n    edges {\n      cursor\n      node {\n        ...on Movie {\n          backdropPath\n          posterPath\n          movieStatus: status\n          title\n          voteAverage\n          voteCount\n        }\n        ...on Tv {\n          backdropPath\n          name\n          posterPath\n          tvStatus: status\n          voteAverage\n          voteCount\n        }\n      }\n    }\n    pageInfo {\n      hasNextPage\n      hasPreviousPage\n      startCursor\n      endCursor\n    }\n    totalCount\n  }\n}\n";

export const TrendingCarousel = ({ children, setActivePreview, setActiveVideo }: TrendingCarouselProps) => {
  const { config } = useToolbelt();
  const theme = useTheme();

  const { execute, loading, result } = useQuery<TrendingData>(GET_TRENDING, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { first: 20 } }, { data: { initiator: 'TrendingCarousel' } });
    // We only want to execute on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  const items = formatTrending(result?.data, config);

  if (!loading && items.length === 0) {
    return;
  }

  const tabsCarousel = (
    <>
      <TabsCarousel
        className="trendingTabsCarousel"
        imageType="backdrop"
        renderTabImage={({ tabSrc, ...rest }) => (
          <BackdropImage
            {...rest}
            certificationSx={{
              [theme.breakpoints.up('sm')]: {
                display: 'none',
              },
            }}
            ribbonSx={{
              [theme.breakpoints.up('sm')]: {
                display: 'none',
              },
            }}
            setActivePreview={setActivePreview}
            setActiveVideo={setActiveVideo}
            showCertification
            showRibbon
            src={tabSrc}
            sx={{
              height: '134px',
              [theme.breakpoints.down('md')]: {
                height: '100px',
              },
              [theme.breakpoints.down('sm')]: {
                height: '159px',
              },
              img: { border: '1px solid', borderColor: theme.palette.grey[900] },
            }}
            type={mapScreenTypeToSectionType(rest.type)}
            videoButtonSx={{
              [theme.breakpoints.up('sm')]: {
                display: 'none',
              },
            }}
            videoComponent="div"
          />
        )}
        renderTabPanel={({ id, panelSrc, status, title, type }) => (
          <TrendingTabPanel
            id={id}
            setActivePreview={setActivePreview}
            setActiveVideo={setActiveVideo}
            src={panelSrc}
            status={status}
            title={title}
            type={type}
          />
        )}
        setActivePreview={setActivePreview}
        tabSx={{
          [theme.breakpoints.down('md')]: {
            width: '180px',
          },
          [theme.breakpoints.down('sm')]: {
            opacity: 1,
            width: '280px',
          },
          overflow: 'visible',
          width: '240px',
        }}
        tabs={items}
      />
    </>
  );

  return children ? children(tabsCarousel) : tabsCarousel;
};
